import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Chip,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Send,
  PictureAsPdf,
  AttachFile,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { quotationAPI } from '../../services/api';

/**
 * SendQuotationDialog
 *
 * Compose-email dialog. The full email body (greeting + quotation summary +
 * footer) is fetched from the server's email-preview endpoint, dropped into a
 * single contentEditable area, and sent verbatim as the email body. The user
 * can edit anything inline — there is no separate preview tab.
 *
 * Props:
 *  - open: boolean
 *  - onClose(): void  (ignored while sending)
 *  - quotationId: string | null
 *  - initialRecipients: [{ name, email }]
 *  - initialSubject: string
 *  - initialFrom?: string                       sender email (logged-in user)
 *  - initialMessage?: string                    fallback paragraph
 *  - initialAttachPdf?: boolean (default true)
 *  - pdfLabel?: string                          e.g. "Quotation - S12345.pdf"
 *  - onBeforeSend?: () => Promise<string>       returns the effective quotationId
 *  - onSent?: ({ quotationId }) => void
 */
const DEFAULT_MESSAGE =
  'Thank you for your interest. Please find attached the quotation as per your requirements.';

// Pull just the inner content of <body>...</body> out of a full email HTML
// document so we can drop it into a contentEditable container without nesting
// <html>/<head>/<body> tags (which the browser would strip anyway).
function extractBodyHtml(fullHtml) {
  if (!fullHtml) return '';
  const match = /<body[^>]*>([\s\S]*)<\/body>/i.exec(fullHtml);
  return match ? match[1] : fullHtml;
}

// Humanise an email local-part into a display name when no name is available:
//   "nandini.kashyap@tech4logic.com" -> "Nandini Kashyap"
function nameFromEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const local = email.split('@')[0] || '';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

// Pick the greeting name from the first recipient (its `name` if it looks
// like a real name, otherwise derived from its email).
function pickGreetingName(recipients) {
  const first = recipients && recipients[0];
  if (!first) return '';
  const rawName = (first.name || '').trim();
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawName);
  if (rawName && !isEmail) return rawName;
  return nameFromEmail(first.email || rawName);
}

const SendQuotationDialog = ({
  open,
  onClose,
  quotationId,
  initialRecipients = [],
  initialSubject = '',
  initialFrom = '',
  initialMessage = DEFAULT_MESSAGE,
  initialAttachPdf = true,
  pdfLabel,
  onBeforeSend,
  onSent,
}) => {
  const [recipients, setRecipients] = useState(initialRecipients);
  const [subject, setSubject] = useState(initialSubject);
  const [fromEmail, setFromEmail] = useState(initialFrom);
  const [attachPdf, setAttachPdf] = useState(initialAttachPdf);
  const [recipientInput, setRecipientInput] = useState('');
  const [sending, setSending] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  // Fetched email body HTML stored in state. We apply it imperatively to the
  // contentEditable via an effect (and a callback ref) so the DOM write
  // happens whenever both the html and the ref are available, regardless of
  // which one settles first.
  const [editorHtml, setEditorHtml] = useState('');
  const editorRef = useRef(null);
  const setEditorNode = useCallback((node) => {
    editorRef.current = node;
    if (node && editorHtml) {
      node.innerHTML = editorHtml;
    }
  }, [editorHtml]);

  // Re-sync from props each time the dialog opens.
  useEffect(() => {
    if (open) {
      setRecipients(initialRecipients);
      setSubject(initialSubject);
      setFromEmail(initialFrom);
      setAttachPdf(initialAttachPdf);
      setRecipientInput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch the email body whenever the dialog opens (or quotation/recipient
  // changes). Stores the HTML in state — a separate effect handles DOM
  // injection.
  useEffect(() => {
    if (!open) return;

    const greetingName = pickGreetingName(recipients);

    if (!quotationId) {
      setEditorHtml(`<p>${initialMessage || DEFAULT_MESSAGE}</p>`);
      return;
    }

    let cancelled = false;
    setEditorLoading(true);
    quotationAPI
      .previewEmail(quotationId, {
        message: initialMessage || '',
        recipientName: greetingName || undefined,
      })
      .then((res) => {
        if (cancelled) return;
        const html = extractBodyHtml(res.data?.html || '');
        setEditorHtml(html || `<p>${initialMessage || DEFAULT_MESSAGE}</p>`);
      })
      .catch((err) => {
        console.error('Failed to load email body:', err);
        if (!cancelled) {
          setEditorHtml(`<p>${initialMessage || DEFAULT_MESSAGE}</p>`);
        }
      })
      .finally(() => {
        if (!cancelled) setEditorLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quotationId, recipients]);

  // Inject the loaded HTML into the contentEditable as soon as the DOM node
  // exists (or the HTML changes). React doesn't manage innerHTML on
  // contentEditable elements, so this imperative write is intentional.
  useEffect(() => {
    if (editorRef.current && editorHtml) {
      editorRef.current.innerHTML = editorHtml;
    }
  }, [editorHtml]);

  const handleClose = () => {
    if (sending) return;
    onClose && onClose();
  };

  const handleRemoveRecipient = (index) => {
    setRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddRecipient = (e) => {
    if (e.key === 'Enter' && recipientInput.trim()) {
      e.preventDefault();
      const email = recipientInput.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      if (recipients.some((r) => r.email === email)) {
        toast.warning('Recipient already added');
        setRecipientInput('');
        return;
      }
      setRecipients((prev) => [...prev, { name: email, email }]);
      setRecipientInput('');
    }
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    if (fromEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fromEmail)) {
      toast.error('Please enter a valid sender email');
      return;
    }

    setSending(true);
    try {
      let effectiveId = quotationId;
      if (onBeforeSend) {
        effectiveId = await onBeforeSend();
      }
      if (!effectiveId) {
        throw new Error('Quotation must be saved before sending');
      }

      const editedHtml = editorRef.current?.innerHTML || '';

      // Send the edited HTML as `body` so the server uses it verbatim instead
      // of feeding it through the email template again.
      await quotationAPI.sendEmail(effectiveId, {
        to: recipients.map((r) => r.email),
        from: fromEmail || undefined,
        subject,
        body: editedHtml,
        attachPdf,
      });

      toast.success('Email sent successfully');
      onSent && onSent({ quotationId: effectiveId });
      onClose && onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || 'Failed to send email'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>Compose Email</Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#666' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* From */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            px: 2,
            py: 1,
          }}
        >
          <Typography sx={{ color: '#666', fontSize: '14px', mr: 2, minWidth: '70px' }}>
            From:
          </Typography>
          <TextField
            fullWidth
            variant="standard"
            placeholder="sender@tech4logic.com"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '14px' },
            }}
          />
        </Box>

        {/* Recipients */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            px: 2,
            py: 1,
          }}
        >
          <Typography sx={{ color: '#666', fontSize: '14px', mr: 2, minWidth: '70px' }}>
            To:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, alignItems: 'center' }}>
            {recipients.map((recipient, index) => (
              <Chip
                key={index}
                label={recipient.name || recipient.email}
                onDelete={() => handleRemoveRecipient(index)}
                size="small"
                sx={{
                  backgroundColor: '#e8e8e8',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '16px',
                  },
                }}
              />
            ))}
            <TextField
              variant="standard"
              placeholder={recipients.length === 0 ? 'Enter email address' : 'Add another'}
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyDown={handleAddRecipient}
              InputProps={{
                disableUnderline: true,
                sx: { fontSize: '14px', minWidth: '150px' },
              }}
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>

        {/* Subject */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            px: 2,
            py: 1,
          }}
        >
          <Typography sx={{ color: '#666', fontSize: '14px', mr: 2, minWidth: '70px' }}>
            Subject:
          </Typography>
          <TextField
            fullWidth
            variant="standard"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '14px' },
            }}
          />
        </Box>

        {/* Editable message body (rich HTML) */}
        <Box sx={{ p: 2, position: 'relative' }}>
          {editorLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.6)',
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <CircularProgress size={24} sx={{ color: '#875A7B' }} />
            </Box>
          )}
          <Box
            ref={setEditorNode}
            contentEditable
            suppressContentEditableWarning
            sx={{
              minHeight: 380,
              maxHeight: 540,
              overflowY: 'auto',
              p: 0,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              fontSize: '14px',
              fontFamily: 'Calibri, Arial, sans-serif',
              color: '#333',
              lineHeight: 1.6,
              outline: 'none',
              backgroundColor: '#fff',
              '&:focus': { borderColor: '#875A7B' },
              '& p': { margin: '0 0 12px 0' },
              '& table': { borderCollapse: 'collapse' },
              '& img': { maxWidth: '100%', height: 'auto' },
            }}
          />
        </Box>

        {/* Attachment */}
        {attachPdf && (
          <Box
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Chip
              icon={<PictureAsPdf sx={{ fontSize: '16px !important' }} />}
              label={pdfLabel || 'Quotation.pdf'}
              onDelete={() => setAttachPdf(false)}
              size="small"
              variant="outlined"
              sx={{
                '& .MuiChip-icon': {
                  color: '#d32f2f',
                },
                '& .MuiChip-deleteIcon': {
                  fontSize: '16px',
                },
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid #e0e0e0',
          px: 2,
          py: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={
              sending ? (
                <CircularProgress size={14} sx={{ color: '#fff' }} />
              ) : (
                <Send sx={{ fontSize: '16px !important' }} />
              )
            }
            onClick={handleSend}
            disabled={sending || recipients.length === 0}
            sx={{
              backgroundColor: '#875A7B',
              textTransform: 'none',
              fontSize: '13px',
              height: '32px',
              '&:hover': { backgroundColor: '#714B68' },
              '&.Mui-disabled': {
                backgroundColor: '#c9b2c2',
                color: '#fff',
              },
            }}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={sending}
            sx={{
              color: '#666',
              borderColor: '#ccc',
              textTransform: 'none',
              fontSize: '13px',
              height: '32px',
              '&:hover': { borderColor: '#999', backgroundColor: 'transparent' },
            }}
          >
            Discard
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setAttachPdf((v) => !v)}
            sx={{ color: attachPdf ? '#875A7B' : '#666' }}
            title="Toggle PDF attachment"
          >
            <AttachFile fontSize="small" />
          </IconButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SendQuotationDialog;
